import {
  ChangeDetectorRef,
  Component,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, filter, tap } from 'rxjs';
import {
  WidgetVizTypeChange,
  chartTypeIdToVisualizationTypeMap,
} from 'src/app/core/models/widget.models';

import { AppState } from 'src/app/reducers';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralDynamicFormComponent } from '../../../general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { Store } from '@ngrx/store';
import { Widget } from 'src/app/core/models/core.models';
import { WidgetSettingsService } from 'src/app/core/services/widget.settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-widget-settings',
  templateUrl: './widget-settings.component.html',
  styleUrls: ['./widget-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WidgetSettingsComponent extends GeneralDynamicFormComponent {
  @Input() widget: Widget;
  @Input() vizTypeChange$: Subject<WidgetVizTypeChange>;
  @Input() massUpdate: boolean = false;
  @Input() widgets: Widget[] = [];

  constructor(
    protected formService: FormsService,
    protected widgetSettings: WidgetSettingsService,
    protected cdf: ChangeDetectorRef,
    protected store: Store<AppState>,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  ngOnInit(): void {
    if (!this.massUpdate) {
      this.filterApplicableSingleWidgetProperties(
        this.widget.widget_type_id,
        this.widget.settings.chart_type_id,
      );
    } else {
      this.filterApplicableMassUpdateProperties();
    }

    super.ngOnInit();

    if (!this.massUpdate) {
      this.formService.populateFormValues(this.formGroup, this.widget);
    } else {
      const commonValues = this.getCommonWidgetValues(this.widgets);
      this.formService.populateFormValues(
        this.formGroup,
        commonValues,
        undefined,
        false,
      );
    }

    if (this.vizTypeChange$) {
      this.vizTypeChange$
        .pipe(
          filter(Boolean),
          tap((val) => {
            this.filterApplicableSingleWidgetProperties(
              val.widgetTypeId,
              val.charTypeId,
            );
            this.cdf.detectChanges();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  filterApplicableSingleWidgetProperties(
    widgetTypeId: number,
    chartTypeId: number,
  ): void {
    const vizTypeId = chartTypeIdToVisualizationTypeMap.get(chartTypeId);
    this.formProperties = this.widgetSettings.filterFormProperties(
      this.formProperties,
      widgetTypeId,
      vizTypeId,
      chartTypeId,
    );
  }

  getCommonWidgetValues(widgets: Widget[]): Record<string, any> {
    if (!widgets.length) return {};

    const flattened = widgets.map((w) => this.formService.flattenObject(w));
    const commonValues: Record<string, any> = {};
    const keys = Object.keys(this.formGroup.controls);

    for (const key of keys) {
      const firstVal = flattened[0][key];
      let isCommon = true;

      // Start from the second widget; exit early if a difference is found
      for (let i = 1; i < flattened.length; i++) {
        if (!this.isEqual(flattened[i][key], firstVal)) {
          isCommon = false;
          break;
        }
      }

      commonValues[key] = isCommon ? firstVal : null;
    }

    return commonValues;
  }

  private isEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a === 'object' && typeof b === 'object') {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return false;
  }

  filterApplicableMassUpdateProperties(): void {
    const widetTypeIds = this.widgets.map(
      (widget: Widget) => widget.widget_type_id,
    );

    const chartTypeIds = this.widgets.map(
      (widget: Widget) => widget.chart_type.id,
    );

    this.formProperties = this.widgetSettings.getMassUpdateFormProperties(
      this.formProperties,
      widetTypeIds,
      chartTypeIds,
    );
  }
}
